import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AppModal from "components/ui/AppModal";
import AppInput from "components/ui/AppInput";
import AppSelect from "components/ui/AppSelect";
import AppSearchInput from "components/ui/AppSearchInput";
import AppButton from "components/ui/AppButton";
import AppOutlineButton from "components/ui/AppOutlineButton";
import { UTILS } from "utils";
import {
  useAddCitiesMutation,
  useGetAddedCitiesMutation,
} from "redux/shipping-methods/shipping-service";
import { useSelector, useDispatch } from "react-redux";
import { AreasAction } from "redux/actions";
import { calculateDeliveryTimeInMins } from "features/shipping_methods/helpers";
import useCurrentLang from "hooks/useCurrentLang";

const initialState = {
  minOrder: "",
  deliveryFee: "",
  deliveryTime: { Hours: 0, Minutes: 0, Days: 0 },
};

const AddNewCity = ({ open, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [getAddedCities] = useGetAddedCitiesMutation();
  const [addCities] = useAddCitiesMutation();
  const language = useCurrentLang();
  const { selectedStore, selectedCities, customizedCityList } = useSelector(
    (state) => state.AreasReducer
  );
  const { currentLanguage } = useSelector((state) => state.LanguageSlice);
  const { isLoading } = useSelector((state) => state.LoaderReducer);
  const [searchTerm, setSearch] = useState("");
  const [state, setState] = useState(initialState);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setState(initialState);
    dispatch(AreasAction.updateSelectedCities([]));
  }, [open, dispatch]);

  const handleValues = (key, value) => {
    setState({ ...state, [key]: value });
  };
  const handleTimeValues = (key, value) => {
    setState((prev) => ({
      ...prev,
      deliveryTime: {
        ...prev.deliveryTime,
        [key]: Number(value || 0),
      },
    }));
  };

  const addNewCities = UTILS.withTryCatch(async () => {
    const delTime = calculateDeliveryTimeInMins({
      deliveryTime: state.deliveryTime,
    });
    const userDetail = UTILS.getCurrentUser();
    const areas = [];
    selectedCities.forEach((city) => {
      areas.push({
        cityID: city._id,
        countryID: city.countryID,
        deliveryFee: +Number(state.deliveryFee),
        deliveryTime: +Number(delTime),
        minOrder: +Number(state.minOrder),
      });
    });
    const obj = {
      brandId: userDetail.brandId,
      storeId: selectedStore.value,
      language: currentLanguage,
      areas,
    };
    const getObj = {
      brandId: userDetail.brandId,
      storeId: selectedStore.value,
    };

    await addCities(obj).unwrap();
    await getAddedCities(getObj).unwrap();
    dispatch(AreasAction.updateSelectedCities([]));
    dispatch(AreasAction.setSync());
    onClose();
  });

  const handleSearch = (key, val) => {
    setSearch(val.toLowerCase());
  };

  const CITIES = useMemo(() => {
    const lowerCaseSearchTerm = searchTerm?.toLowerCase()?.trim() ?? "";
    if (!lowerCaseSearchTerm) {
      return customizedCityList;
    }

    return customizedCityList.filter(
      (item) =>
        item?.cityNameEN?.toLowerCase()?.includes(lowerCaseSearchTerm) ||
        item?.cityNameAR?.toLowerCase()?.includes(lowerCaseSearchTerm)
    );
  }, [customizedCityList, searchTerm]);

  const saveArea = (city) => {
    let update = [];
    const isCityAlreadySelected = !!selectedCities?.find(
      (c) => c?._id === city?._id
    );
    if (isCityAlreadySelected) {
      update = selectedCities.filter((c) => c._id !== city._id);
    } else {
      update = [...selectedCities, city];
    }
    dispatch(AreasAction.updateSelectedCities(update));
  };

  const isChecked = (city) => {
    return !!selectedCities?.find((c) => c?._id === city?._id);
  };

  const isValid = () => {
    if (
      isLoading ||
      state?.minOrder === "" ||
      state?.deliveryFee === "" ||
      selectedCities.length <= 0 ||
      (state?.deliveryTime?.Days === 0 &&
        state?.deliveryTime?.Hours === 0 &&
        state?.deliveryTime?.Minutes === 0)
    )
      return false;
    return true;
  };
  const handleInputValue = (key, val) => {
    const value = UTILS.NumberConversion(val);
    handleValues(key, value);
  };
  const daysOptions = Array.from({ length: 8 }, (_, value) => ({
    label: `${value} ${t("Days")}`,
    value,
  }));
  const hourOptions = Array.from({ length: 24 }, (_, value) => ({
    label: `${value} ${t("Hours")}`,
    value,
  }));
  const minuteOptions = Array.from({ length: 12 }, (_, index) => ({
    label: `${index * 5} ${t("Minutes")}`,
    value: index * 5,
  }));

  const footer = (
    <div className="flex justify-end gap-2">
      <AppOutlineButton variant="secondary" onClick={onClose} disabled={isLoading}>
        {t("Cancel")}
      </AppOutlineButton>
      <AppButton
        variant="primary"
        onClick={addNewCities}
        disabled={!isValid()}
        loading={isLoading}
      >
        {t("Add Cities")}
      </AppButton>
    </div>
  );

  return (
    <AppModal
      title={t("Add delivery cities")}
      open={open}
      onClose={onClose}
      width={650}
      footer={footer}
      loading={isLoading}
    >
      <div className="grid grid-cols-2 gap-4">
        <AppInput
          label={t("Min. Basket Value")}
          value={state.minOrder}
          onInput={(e) => handleInputValue("minOrder", e.target.value)}
        />
        <AppInput
          label={t("Delivery Fee")}
          value={state.deliveryFee}
          onInput={(e) => handleInputValue("deliveryFee", e.target.value)}
        />
      </div>
      <div className="my-3 text-lg font-bold">{t("Delivery Time")}</div>
      <div className="grid grid-cols-3 gap-4">
        <AppSelect
          placeholder={t("Select Days")}
          value={state.deliveryTime.Days}
          options={daysOptions}
          onChange={(value) => handleTimeValues("Days", value)}
        />
        <AppSelect
          placeholder={t("Select Hours")}
          value={state.deliveryTime.Hours}
          options={hourOptions}
          onChange={(value) => handleTimeValues("Hours", value)}
        />
        <AppSelect
          placeholder={t("Select Minutes")}
          value={state.deliveryTime.Minutes}
          options={minuteOptions}
          onChange={(value) => handleTimeValues("Minutes", value)}
        />
      </div>
      <div className="my-3 text-lg font-bold">
        <span className="mb-2 block">{t("Search City Name")}</span>
        <AppSearchInput
          value={searchTerm}
          placeholder={t("Search")}
          onInput={(e) => handleSearch("searchTerm", e.target.value)}
        />
      </div>
      <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 p-2">
        {CITIES?.length === 0 ? (
          <div className="p-2 text-sm text-gray-500">{t("No cities found")}</div>
        ) : (
          CITIES.map((city) => {
            const selected = isChecked(city);
            const name = language === "en" ? city?.cityNameEN : city?.cityNameAR;
            return (
              <button
                key={city?._id}
                type="button"
                className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left hover:bg-gray-50"
                onClick={() => saveArea(city)}
              >
                <div className="text-sm font-medium text-gray-800">{name}</div>
                <span
                  className={`block h-5 w-5 rounded border ${
                    selected ? "bg-blue-500 border-blue-500" : "bg-white border-gray-300"
                  }`}
                />
              </button>
            );
          })
        )}
      </div>
    </AppModal>
  );
};
export default AddNewCity;
